import { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Button, Input } from '../ui';
import { getLevelOptions } from '../../data/levelOptions';
import { getLadderDrillOptions } from '../../data/ladderDrills';
import { parseDefaultValues } from '../../data/trainingSessionHelpers';
import { cn } from '../../utils/cn';

export function DocumentationModal({
  isOpen,
  onClose,
  onSave,
  phaseNum,
  superset,
  setNumber, // 1, 2, or 3
  existingSetData, // { set1: {...}, set2: {...}, set3: {...} }
  completedSets, // [1, 2] etc
}) {
  const [formData, setFormData] = useState({
    exercise1: {},
    exercise2: {},
  });
  const [applyToSet2, setApplyToSet2] = useState(false);
  const [applyToSet3, setApplyToSet3] = useState(false);
  const [expandedSet, setExpandedSet] = useState(null);

  const { exercise1, exercise2 } = superset;

  // Initialize form data
  useEffect(() => {
    if (isOpen) {
      const lastCompletedSet = completedSets.length > 0 
        ? existingSetData[`set${Math.max(...completedSets)}`]
        : null;

      setFormData({
        exercise1: getInitialValues(exercise1, lastCompletedSet?.exercise1),
        exercise2: getInitialValues(exercise2, lastCompletedSet?.exercise2),
      });

      // Reset apply checkboxes
      setApplyToSet2(false);
      setApplyToSet3(false);
    }
  }, [isOpen, setNumber]);

  const getInitialValues = (exercise, lastSetData) => {
    // If we have data from a previous set, use that
    if (lastSetData) {
      return { ...lastSetData };
    }

    // Otherwise parse defaults from documentation
    const defaults = parseDefaultValues(exercise.documentation);
    const initial = {};

    if (exercise.hasLevels && exercise.levelNumber) {
      initial.level = 'Level 1';
    }

    if (exercise.isLadderDrill) {
      initial.drill1 = '';
      initial.drill2 = '';
      initial.drill3 = '';
    }

    if (exercise.hasSide) {
      initial.left = {};
      initial.right = {};
      
      if (exercise.hasReps) {
        initial.left.reps = defaults.reps || '';
        initial.right.reps = defaults.reps || '';
      }
      if (exercise.hasHoldTime) {
        initial.left.holdTime = defaults.holdTime || '';
        initial.right.holdTime = defaults.holdTime || '';
      }
      if (exercise.hasWeight) {
        initial.left.weight = '';
        initial.right.weight = '';
      }
      if (exercise.hasBoxHeight) {
        initial.left.boxHeight = '';
        initial.right.boxHeight = '';
      }
      if (exercise.hasHurdleHeight) {
        initial.left.hurdleHeight = '';
        initial.right.hurdleHeight = '';
      }
      if (exercise.hasBandColor) {
        initial.left.bandColor = '';
        initial.right.bandColor = '';
      }
    } else {
      if (exercise.hasReps) {
        initial.reps = defaults.reps || '';
      }
      if (exercise.hasHoldTime) {
        initial.holdTime = defaults.holdTime || '';
      }
      if (exercise.hasWeight) {
        initial.weight = '';
      }
      if (exercise.hasBoxHeight) {
        initial.boxHeight = '';
      }
      if (exercise.hasHurdleHeight) {
        initial.hurdleHeight = '';
      }
      if (exercise.hasBandColor) {
        initial.bandColor = '';
      }
    }

    initial.notes = '';

    return initial;
  };

  const updateExerciseData = (exerciseKey, field, value, side = null) => {
    setFormData(prev => {
      const exerciseData = { ...prev[exerciseKey] };
      
      if (side) {
        exerciseData[side] = {
          ...exerciseData[side],
          [field]: value,
        };
      } else {
        exerciseData[field] = value;
      }

      return {
        ...prev,
        [exerciseKey]: exerciseData,
      };
    });
  };

  const handleSave = () => {
    const setsToSave = [setNumber];
    if (applyToSet2 && setNumber < 2) setsToSave.push(2);
    if (applyToSet3 && setNumber < 3) setsToSave.push(3);

    onSave(formData, setsToSave);
    onClose();
  };

  const renderExerciseFields = (exercise, exerciseKey) => {
    const data = formData[exerciseKey];
    const levelOptions = exercise.hasLevels ? getLevelOptions(exercise.levelNumber) : [];
    const ladderOptions = exercise.isLadderDrill ? getLadderDrillOptions(phaseNum) : [];

    return (
      <div className="space-y-4">
        {/* Level Selection */}
        {exercise.hasLevels && levelOptions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
            <div className="flex flex-wrap gap-2">
              {levelOptions.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => updateExerciseData(exerciseKey, 'level', level)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                    data.level === level
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  )}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ladder Drill Selection */}
        {exercise.isLadderDrill && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Select 3 Drills</label>
            {[1, 2, 3].map((num) => (
              <select
                key={num}
                value={data[`drill${num}`] || ''}
                onChange={(e) => updateExerciseData(exerciseKey, `drill${num}`, e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Drill {num}</option>
                {ladderOptions.map((drill) => (
                  <option key={drill} value={drill}>{drill}</option>
                ))}
              </select>
            ))}
          </div>
        )}

        {/* Side-specific fields */}
        {exercise.hasSide ? (
          <div className="grid grid-cols-2 gap-4">
            {['left', 'right'].map((side) => (
              <div key={side} className="space-y-3">
                <p className="text-sm font-medium text-gray-700 capitalize text-center">{side}</p>
                
                {exercise.hasReps && (
                  <Input
                    label="Reps"
                    type="number"
                    value={data[side]?.reps || ''}
                    onChange={(e) => updateExerciseData(exerciseKey, 'reps', e.target.value, side)}
                    placeholder="0"
                  />
                )}
                
                {exercise.hasHoldTime && (
                  <Input
                    label="Hold (sec)"
                    type="number"
                    value={data[side]?.holdTime || ''}
                    onChange={(e) => updateExerciseData(exerciseKey, 'holdTime', e.target.value, side)}
                    placeholder="0"
                  />
                )}
                
                {exercise.hasWeight && (
                  <Input
                    label="Weight (lbs)"
                    type="number"
                    step="0.01"
                    value={data[side]?.weight || ''}
                    onChange={(e) => updateExerciseData(exerciseKey, 'weight', e.target.value, side)}
                    placeholder="0"
                  />
                )}
                
                {exercise.hasBoxHeight && (
                  <Input
                    label="Box Height"
                    value={data[side]?.boxHeight || ''}
                    onChange={(e) => updateExerciseData(exerciseKey, 'boxHeight', e.target.value, side)}
                    placeholder='e.g., 12"'
                  />
                )}
                
                {exercise.hasHurdleHeight && (
                  <Input
                    label="Hurdle Height"
                    value={data[side]?.hurdleHeight || ''}
                    onChange={(e) => updateExerciseData(exerciseKey, 'hurdleHeight', e.target.value, side)}
                    placeholder='e.g., 6"'
                  />
                )}
                
                {exercise.hasBandColor && (
                  <Input
                    label="Band Color"
                    value={data[side]?.bandColor || ''}
                    onChange={(e) => updateExerciseData(exerciseKey, 'bandColor', e.target.value, side)}
                    placeholder="Color"
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Non-side fields */
          <div className="space-y-3">
            {exercise.hasReps && !exercise.isLadderDrill && (
              <Input
                label="Reps"
                type="number"
                value={data.reps || ''}
                onChange={(e) => updateExerciseData(exerciseKey, 'reps', e.target.value)}
                placeholder="0"
              />
            )}
            
            {exercise.hasHoldTime && (
              <Input
                label="Hold Time (seconds)"
                type="number"
                value={data.holdTime || ''}
                onChange={(e) => updateExerciseData(exerciseKey, 'holdTime', e.target.value)}
                placeholder="0"
              />
            )}
            
            {exercise.hasWeight && (
              <Input
                label="Weight (lbs)"
                type="number"
                step="0.01"
                value={data.weight || ''}
                onChange={(e) => updateExerciseData(exerciseKey, 'weight', e.target.value)}
                placeholder="0"
              />
            )}
            
            {exercise.hasBoxHeight && (
              <Input
                label="Box Height"
                value={data.boxHeight || ''}
                onChange={(e) => updateExerciseData(exerciseKey, 'boxHeight', e.target.value)}
                placeholder='e.g., 12"'
              />
            )}
            
            {exercise.hasHurdleHeight && (
              <Input
                label="Hurdle Height"
                value={data.hurdleHeight || ''}
                onChange={(e) => updateExerciseData(exerciseKey, 'hurdleHeight', e.target.value)}
                placeholder='e.g., 6"'
              />
            )}
            
            {exercise.hasBandColor && (
              <Input
                label="Band Color"
                value={data.bandColor || ''}
                onChange={(e) => updateExerciseData(exerciseKey, 'bandColor', e.target.value)}
                placeholder="Color"
              />
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={data.notes || ''}
            onChange={(e) => updateExerciseData(exerciseKey, 'notes', e.target.value)}
            placeholder="Optional notes..."
            rows={2}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-gray-400 resize-none text-sm"
          />
        </div>
      </div>
    );
  };

  const renderCompletedSetSummary = (setNum) => {
    const setData = existingSetData[`set${setNum}`];
    if (!setData) return null;

    const summarizeExercise = (exData, exercise) => {
      const parts = [];
      
      if (exData.level) parts.push(exData.level);
      
      if (exercise.hasSide) {
        if (exData.left?.reps) parts.push(`L: ${exData.left.reps} reps`);
        if (exData.right?.reps) parts.push(`R: ${exData.right.reps} reps`);
        if (exData.left?.holdTime) parts.push(`L: ${exData.left.holdTime}s`);
        if (exData.right?.holdTime) parts.push(`R: ${exData.right.holdTime}s`);
      } else {
        if (exData.reps) parts.push(`${exData.reps} reps`);
        if (exData.holdTime) parts.push(`${exData.holdTime}s`);
        if (exData.weight) parts.push(`${exData.weight} lbs`);
      }
      
      if (exData.drill1) parts.push(exData.drill1);
      
      return parts.join(', ') || 'No data';
    };

    return (
      <div className="text-sm text-gray-600 space-y-1 mt-2">
        <p><span className="font-medium">Ex 1:</span> {summarizeExercise(setData.exercise1, exercise1)}</p>
        <p><span className="font-medium">Ex 2:</span> {summarizeExercise(setData.exercise2, exercise2)}</p>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />
      
      <div className="flex min-h-full items-end justify-center sm:items-center">
        <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">Document Set {setNumber}</h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Completed Sets Summary */}
            {completedSets.length > 0 && (
              <div className="mb-6">
                {completedSets.map((setNum) => (
                  <div key={setNum} className="mb-2">
                    <button
                      onClick={() => setExpandedSet(expandedSet === setNum ? null : setNum)}
                      className="w-full flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg text-left"
                    >
                      <div className="flex items-center">
                        <Check className="h-4 w-4 text-green-600 mr-2" />
                        <span className="font-medium text-green-800">Set {setNum} Completed</span>
                      </div>
                      {expandedSet === setNum ? (
                        <ChevronUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-green-600" />
                      )}
                    </button>
                    {expandedSet === setNum && renderCompletedSetSummary(setNum)}
                  </div>
                ))}
              </div>
            )}

            {/* Exercise 1 */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {exercise1.name}
              </h3>
              {renderExerciseFields(exercise1, 'exercise1')}
            </div>

            {/* Exercise 2 */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {exercise2.name}
              </h3>
              {renderExerciseFields(exercise2, 'exercise2')}
            </div>

            {/* Apply to other sets */}
            {setNumber < 3 && (
              <div className="space-y-2 mb-4">
                {setNumber < 2 && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={applyToSet2}
                      onChange={(e) => setApplyToSet2(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Apply same values to Set 2</span>
                  </label>
                )}
                {setNumber < 3 && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={applyToSet3}
                      onChange={(e) => setApplyToSet3(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Apply same values to Set 3</span>
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t border-gray-200 flex-shrink-0">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              fullWidth
            >
              Save Set
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}